/**
 * «Юниверситет» (28.09): прогресс оператора по пакету — в D1 (GET/PUT /api/uni/:pack/progress),
 * «не в localStorage». Но «при плохой сети в Telegram держать черновик в localStorage и
 * досылать» (ТЗ): каждая правка сразу пишется в черновик ub_uni_progress_<pack> и уходит
 * PUT'ом; не ушла — повторяем по таймеру, при возврате на экран, при появлении сети и при
 * следующем входе. Слияние — mergeProgress (идемпотентно: повторная досылка не удвоит опыт,
 * ответ дня — первый, серверный). Поле xp — всегда xpOf (опыт считает клиент, сервер хранит).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  achEarned, dailyPick, emptyProgress, mergeProgress, orenDate, xpOf,
  type UniPack, type UniProgress,
} from "@ub/shared/uni";
import { api } from "../../../api/client";
import { lsGet, lsSet } from "./ctx";

interface ProgResp { data: Partial<UniProgress> | null; rev: number; today: string; daily: string[] }

export interface UniProgressApi {
  prog: UniProgress;
  /** последнее локальное состояние без ожидания рендера (для проверок «уже отвечено») */
  get: () => UniProgress;
  ready: boolean;
  /** сегодняшняя дата UTC+5 и вопросы дня — от сервера; без связи — та же формула локально */
  today: string;
  daily: string[];
  /** последняя попытка сохранить не удалась — прогресс лежит в черновике устройства */
  offline: boolean;
  /** применить правку; вернёт id впервые полученных ачивок (для тостов) */
  commit: (fn: (p: UniProgress) => UniProgress) => string[];
}

/* ключ — пакет и сотрудник: на общем телефоне чужой неотправленный черновик не затирается */
const draftKey = (pack: string, owner = "") => `ub_uni_progress_${pack}_${owner}`;
const RETRY_MS = [5_000, 15_000, 30_000, 60_000];

/** Сервер для нового оператора отдаёт {} — достраиваем форму. */
function normalize(d: Partial<UniProgress> | null | undefined): UniProgress {
  const e = emptyProgress();
  if (!d || typeof d !== "object") return e;
  return {
    ...e, ...d, v: 1,
    quests: d.quests && typeof d.quests === "object" ? d.quests : {},
    daily: d.daily && typeof d.daily === "object" ? d.daily : {},
    tracks: Array.isArray(d.tracks) ? d.tracks : [],
    ach: d.ach && typeof d.ach === "object" ? d.ach : {},
  };
}

/* Черновик помечен владельцем: на общем телефоне чужой прогресс к себе не подмешиваем. */
function readDraft(pack: string, owner: string): UniProgress | null {
  const raw = lsGet(draftKey(pack, owner));
  if (!raw) return null;
  try {
    const d = JSON.parse(raw) as { owner?: string; data?: Partial<UniProgress> };
    return d.owner === owner && d.data ? normalize(d.data) : null;
  } catch { return null; }
}
const writeDraft = (pack: string, owner: string, data: UniProgress | null) =>
  lsSet(draftKey(pack, owner), data ? JSON.stringify({ owner, data }) : null);

export function useUniProgress(packId: string, pack: UniPack | null, owner: string): UniProgressApi {
  const [prog, setProg] = useState<UniProgress>(emptyProgress);
  const [ready, setReady] = useState(false);
  const [day, setDay] = useState<{ today: string; daily: string[] } | null>(null);
  const [offline, setOffline] = useState(false);
  const ref = useRef<UniProgress>(prog);
  const packRef = useRef(pack);
  packRef.current = pack;
  const s = useRef({ gen: 0, dirty: false, ver: 0, busy: false, loaded: false, fails: 0, timer: 0, lastGet: 0 });

  const withXp = useCallback((p: UniProgress) => (packRef.current ? { ...p, xp: xpOf(packRef.current, p) } : p), []);
  const apply = useCallback((p: UniProgress) => { ref.current = p; setProg(p); }, []);

  /* Ответ сервера (GET или PUT) — сливаем с тем, что уже есть на устройстве. */
  const take = useCallback((r: ProgResp) => {
    apply(withXp(mergeProgress(normalize(r.data), ref.current)));
    if (r.today) setDay({ today: r.today, daily: Array.isArray(r.daily) ? r.daily : [] });
    s.current.loaded = true;
    s.current.lastGet = Date.now();
  }, [apply, withXp]);

  const sync = useCallback(async (keepalive = false): Promise<void> => {
    const st = s.current;
    if (st.busy) return;
    const gen = st.gen;
    const url = `/api/uni/${encodeURIComponent(packId)}/progress`;
    window.clearTimeout(st.timer);
    st.busy = true;
    try {
      if (st.dirty) {
        const sentVer = st.ver;
        const data = withXp(ref.current);
        const small = JSON.stringify(data).length < 60_000; // лимит тела keepalive-запроса
        /* PUT идемпотентен (слияние на сервере) — повтор безопасен */
        const r = await api<ProgResp>(url, { method: "PUT", body: { data }, retries: 2, keepalive: keepalive && small });
        if (gen !== st.gen) return;
        take(r);
        if (st.ver === sentVer) { st.dirty = false; writeDraft(packId, owner, null); }
        else writeDraft(packId, owner, ref.current);
      } else if (!st.loaded || Date.now() - st.lastGet > 60_000) {
        const r = await api<ProgResp>(url);
        if (gen !== st.gen) return;
        take(r);
      }
      st.fails = 0;
      setOffline(false);
    } catch {
      if (gen !== st.gen) return;
      st.fails += 1;
      setOffline(true);
      st.timer = window.setTimeout(() => void sync(), RETRY_MS[Math.min(st.fails, RETRY_MS.length) - 1] ?? 60_000);
    } finally {
      if (gen === st.gen) {
        st.busy = false;
        setReady(true);
        /* пока летел PUT, успели ещё что-то ответить — досылаем следом */
        if (st.dirty && st.fails === 0) st.timer = window.setTimeout(() => void sync(), 300);
      }
    }
  }, [packId, owner, take, withXp]);

  /* Вход: показать черновик сразу (если есть), затем сверка с сервером. */
  useEffect(() => {
    const st = s.current;
    st.gen += 1;
    Object.assign(st, { dirty: false, ver: 0, busy: false, loaded: false, fails: 0, lastGet: 0 });
    const draft = readDraft(packId, owner);
    apply(draft ?? emptyProgress());
    st.dirty = !!draft;
    setReady(!!draft); // черновик есть — показываем сразу, сверка идёт фоном
    setDay(null);
    void sync();
    return () => {
      st.gen += 1; // ответы старых запросов больше не применяем
      window.clearTimeout(st.timer);
      /* ушли с вкладки раньше, чем сработала досылка, — последняя попытка; не выйдет —
         черновик уйдёт при следующем входе */
      if (st.dirty && !st.busy) {
        void api(`/api/uni/${encodeURIComponent(packId)}/progress`, {
          method: "PUT", body: { data: withXp(ref.current) }, keepalive: true,
        }).catch(() => {});
      }
    };
  }, [packId, owner, apply, sync, withXp]);

  /* Досылка: вернулись на экран / появилась сеть; уход с экрана — последняя попытка. */
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") void sync();
      else if (s.current.dirty) void sync(true);
    };
    const onOnline = () => void sync();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("online", onOnline);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("online", onOnline);
    };
  }, [sync]);

  const commit = useCallback((fn: (p: UniProgress) => UniProgress): string[] => {
    const pk = packRef.current;
    let next = fn(ref.current);
    if (next === ref.current) return [];
    let fresh: string[] = [];
    if (pk) {
      fresh = achEarned(pk, next).filter((id) => !next.ach[id]);
      if (fresh.length) {
        const at = new Date().toISOString();
        next = { ...next, ach: { ...next.ach, ...Object.fromEntries(fresh.map((id) => [id, at])) } };
      }
      next = { ...next, xp: xpOf(pk, next) };
    }
    apply(next);
    const st = s.current;
    st.dirty = true;
    st.ver += 1;
    writeDraft(packId, owner, next);
    if (!st.busy) {
      window.clearTimeout(st.timer);
      st.timer = window.setTimeout(() => void sync(), 600);
    }
    return fresh;
  }, [packId, owner, apply, sync]);

  const get = useCallback(() => ref.current, []);
  const today = day?.today ?? orenDate();
  const daily = day ? day.daily : pack ? dailyPick(pack, today) : [];
  return { prog, get, ready, today, daily, offline, commit };
}
